import React, { useEffect, useState } from "react";
import API from "../api/api";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { Modal, Button, Form } from "react-bootstrap";
import AppShell from "../components/ui/AppShell";
import PageHeader from "../components/ui/PageHeader";
import EmptyState from "../components/ui/EmptyState";

const Permissions = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [currentPermission, setCurrentPermission] = useState({});
  const [formData, setFormData] = useState({ permission_name: "", description: "" });

  const fetchPermissions = async () => {
    try {
      const response = await API.get("/permissions");
      setPermissions(response.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch permissions");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAdd = async () => {
    try {
      await API.post("/permissions", formData);
      fetchPermissions();
      setShowAdd(false);
      setFormData({ permission_name: "", description: "" });
    } catch (err) {
      alert("Failed to add permission");
    }
  };

  const handleEdit = async () => {
    try {
      await API.put(`/permissions/${currentPermission.id}`, formData);
      fetchPermissions();
      setShowEdit(false);
    } catch (err) {
      alert("Failed to update permission");
    }
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/permissions/${currentPermission.id}`);
      fetchPermissions();
      setShowDelete(false);
    } catch (err) {
      alert("Failed to delete permission");
    }
  };

  return (
    <AppShell>
      <PageHeader
        title="Permissions"
        description="Create and manage access permissions."
        actions={
          <Button variant="primary" onClick={() => setShowAdd(true)}>
            <FaPlus /> Add Permission
          </Button>
        }
      />

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="vp-loading">Loading permissions…</div>
      ) : permissions.length === 0 ? (
        <EmptyState
          title="No permissions yet"
          description="Add permissions that can be attached to roles."
          action={
            <Button variant="primary" onClick={() => setShowAdd(true)}>
              <FaPlus /> Add Permission
            </Button>
          }
        />
      ) : (
        <div className="vp-table-wrap table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>ID</th>
                <th>Permission Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {permissions.map((perm) => (
                <tr key={perm.id}>
                  <td>{perm.id}</td>
                  <td>{perm.permission_name}</td>
                  <td>{perm.description}</td>
                  <td>
                    <div className="vp-table-actions">
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() => {
                          setCurrentPermission(perm);
                          setFormData({
                            permission_name: perm.permission_name,
                            description: perm.description,
                          });
                          setShowEdit(true);
                        }}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          setCurrentPermission(perm);
                          setShowDelete(true);
                        }}
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={showAdd} onHide={() => setShowAdd(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Permission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Permission Name</Form.Label>
              <Form.Control
                type="text"
                name="permission_name"
                value={formData.permission_name}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdd(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            Add
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEdit} onHide={() => setShowEdit(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Permission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Permission Name</Form.Label>
              <Form.Control
                type="text"
                name="permission_name"
                value={formData.permission_name}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEdit}>
            Update
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDelete} onHide={() => setShowDelete(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Permission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{currentPermission.permission_name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </AppShell>
  );
};

export default Permissions;
